<template>
    <!-- Même carte que « Consultations » (styles partagés), affichée seulement pour un médecin d'une spécialité suivie par séances. -->
    <section v-if="visible" class="consultations-medecin kine-dashboard card mb-4" aria-labelledby="kine-reeducations-title">
        <div class="card-body">
            <div class="med-card-title">
                <h3 id="kine-reeducations-title" class="c-title text-primary mb-0">Rééducations</h3>
                <button type="button" class="med-text-button med-actualiser" title="Actualiser" aria-label="Actualiser les rééducations" :disabled="loading" @click="load">
                    <i class="fa fa-sync-alt" :class="{ 'fa-spin': loading }" aria-hidden="true"></i>
                </button>
            </div>
            <p class="med-hint">Séances de rééducation : progression, dernière séance et état de la prochaine.</p>
            <div class="med-tabs" role="tablist" aria-label="Rééducations">
                <button
                    v-for="item in tabs"
                    :key="item.value"
                    type="button"
                    role="tab"
                    :id="'kine-tab-' + item.value"
                    aria-controls="kine-reeducations-panel"
                    :aria-selected="tab === item.value ? 'true' : 'false'"
                    :disabled="loading"
                    @click="changeTab(item.value)"><span class="med-tab-count">{{ (result.compteurs || {})[item.value] || 0 }}</span>{{ item.label }}</button>
            </div>
            <div id="kine-reeducations-panel" role="tabpanel" :aria-labelledby="'kine-tab-' + tab" :aria-busy="loading ? 'true' : 'false'">
                <div class="med-filters">
                    <input
                        v-model="search"
                        type="search"
                        maxlength="120"
                        class="input-secondary-light"
                        placeholder="Rechercher : patient ou prescription"
                        aria-label="Rechercher un patient ou une prescription"
                        autocomplete="off"
                        @input="rechercher"
                        @keydown.enter.prevent="page = 1; load()">
                </div>
                <div v-if="error" class="med-error" role="alert">{{ error }}</div>
                <p v-if="loading" role="status">Chargement des rééducations…</p>
                <div v-else-if="(result.data || []).length" class="med-table table-responsive">
                    <table class="table b-table">
                        <thead><tr><th>Patient</th><th>Prescription</th><th>Progression</th><th>Dernière séance</th><th>Prochaine séance</th><th class="med-actions-cell">Actions</th></tr></thead>
                        <tbody>
                            <tr v-for="row in result.data" :key="row.id">
                                <td data-label="Patient"><strong>{{ row.patient.nom }} {{ row.patient.prenoms }}</strong><small class="d-block">{{ row.patient.code }}</small></td>
                                <td data-label="Prescription">{{ row.motif || 'Motif non renseigné' }}<small class="d-block">{{ row.prescripteur || 'Prescripteur non renseigné' }} · {{ total(row) }} séances</small></td>
                                <td data-label="Progression"><strong>{{ row.faites }} / {{ total(row) }}</strong><progress :value="row.faites" :max="total(row) || 1" :aria-label="row.faites + ' séances faites sur ' + total(row)"></progress></td>
                                <td data-label="Dernière séance"><template v-if="row.derniere_seance">{{ date(row.derniere_seance.date) }}<small class="d-block">{{ recorded(row.derniere_seance.douleur_avant) }} → {{ recorded(row.derniere_seance.douleur_apres) }} · {{ row.derniere_seance.tolerance || 'Tolérance non renseignée' }}</small></template><span v-else>Aucune séance faite</span></td>
                                <td data-label="Prochaine séance"><span class="med-badge" :class="tone((row.prochaine || {}).etat)">{{ states[(row.prochaine || {}).etat] || 'État indisponible' }}</span><small v-if="(row.prochaine || {}).date_heure" class="d-block mt-1">{{ date(row.prochaine.date_heure, row.prochaine.heure_a_fixer) }}</small></td>
                                <td data-label="Actions" class="med-actions-cell"><button type="button" class="med-text-button med-action" :disabled="!!actionLoading" @click="act(row)">{{ action(row) }}</button></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-else-if="!error" class="med-hint">Aucune rééducation dans cet onglet.</p>
                <b-pagination v-if="result.total > result.per_page" v-model="page" :total-rows="result.total" :per-page="result.per_page" :disabled="loading" aria-label="Pages des rééducations" @input="load" />
            </div>
            <ChoixFicheConsultation ref="demarrage" :user_id="Number(user.id)" :user_info="{ id: Number(user.id), user: user.name, titre: user.titre }" @demarree="navigate" />
            <RendezVous ref="rendezVous" :target="rdvTarget" :reprogrammer="reprogram" @enregistre="load" @erreur="error = $event" />
        </div>
    </section>
</template>
<script>
import { BPagination } from 'bootstrap-vue'
import api from '@/api/_kine-clinical'
import consultations from '@/store/mods/consultations/consultations'
import ChoixFicheConsultation from '@/views/consultations/en-cours/choix-fiche-consultation'
import RendezVous from '@/views/consultations/en-cours/rendez-vous'
import { tabs, states, actionFor, totalSessions, clinicalToken, message, nextSessionDate } from '@/utils/kine-reeducation'
import { clinicNow, clinicalDate } from '@/utils/kine-clinical-time'
export default {
    name: 'KineDashboard', components: { BPagination, ChoixFicheConsultation, RendezVous },
    data: () => ({ tabs, states, tab: 'aujourdhui', page: 1, search: '', result: {}, loading: false, error: '', sequence: 0,
        actionLoading: null, rdvTarget: null, reprogram: false, visible: false, rechercheTimer: null }),
    computed: {
        user() { return this.$store.getters.authInfo || {} },
        identity() { const role = this.$store.getters.activeRole || {}; return JSON.stringify([this.user.id, this.user.access_token, role.id, role.nom]) }
    },
    watch: { identity() { this.sequence++; this.result = {}; this.rdvTarget = null; this.load() } },
    mounted() { this.load(); window.addEventListener('clinicaa:consultation-updated', this.load) },
    beforeDestroy() { this.sequence++; clearTimeout(this.rechercheTimer); window.removeEventListener('clinicaa:consultation-updated', this.load) },
    methods: {
        total: totalSessions, action: actionFor,
        recorded(value) { return value === null || value === undefined ? 'Non renseignée' : value },
        date(value, dateOnly) {
            if (!value) return 'Date non renseignée'
            if (dateOnly) return clinicNow(new Date(value), this.result.fuseau).slice(0, 10).split('-').reverse().join('/') + ' · heure à fixer'
            return clinicalDate(value, this.result.fuseau)
        },
        changeTab(tab) { this.tab = tab; this.page = 1; this.load() },
        rechercher() {
            clearTimeout(this.rechercheTimer)
            this.rechercheTimer = setTimeout(() => { this.page = 1; this.load() }, 350)
        },
        tone(etat) {
            if (['payee', 'planifiee_payee_avance'].includes(etat)) return 'med-tone-success'
            if (['planifiee_attente_caisse', 'heure_a_fixer'].includes(etat)) return 'med-tone-warning'
            if (['absent', 'annule'].includes(etat)) return 'med-tone-danger'
            return etat === 'en_cours' ? 'med-tone-primary' : 'med-tone-muted'
        },
        async load() {
            const sequence = ++this.sequence, identity = this.identity
            const current = () => !this._isDestroyed && sequence === this.sequence && identity === this.identity
            this.loading = true; this.error = ''
            try {
                const token = await clinicalToken(current)
                const response = await api.getTableau({ onglet: this.tab, page: this.page, search: this.search.trim() }, token)
                if (!current()) return
                this.$emit('specialites', response.specialites)
                this.$emit('cartes', response.cartes)
                // La carte n'apparaît que pour un médecin d'une spécialité suivie par séances.
                this.visible = this.visible || ((response.specialites || {}).suivies || []).length > 0
                clinicNow(new Date(), response.fuseau)
                this.result = response; this.$emit('erreur', '')
            } catch (error) { if (current()) { this.error = message(error); this.$emit('erreur', this.error) } }
            finally { if (current()) this.loading = false }
        },
        navigate(item) {
            this.$store.commit('SET_SELECTED_CONSULTATION_ENCOURS', { consultation_id: item.consultation_id,
                patient_id: item.patient_id, tarif_consultation_id: item.tarif_consultation_id, source_tableau_medecin: true })
            return this.$router.push({ path: '/patients/' + item.patient_id + '/consulter', query: { med004: String(item.consultation_id), tarif: String(item.tarif_consultation_id) } })
        },
        async act(row) {
            if (this.actionLoading) return
            const identity = this.identity, current = () => !this._isDestroyed && this.identity === identity
            this.actionLoading = row.id; this.error = ''
            try {
                const state = (row.prochaine || {}).etat
                if (['en_cours', 'payee'].includes(state)) {
                    const access = await consultations.getTableauMedecinAccess()
                    if (!current()) return
                    if (!access || !access.success || !access.data || !access.data.access_token) throw Error('L’autorisation médecin est indisponible.')
                    // Reprendre la sélection du tableau existant, qui fournit le tarif exact exigé à l’ouverture.
                    // « En attente » / « En cours » portent les bons du kiné ; « Toutes » ceux de la spécialité attribués à d’autres.
                    let found = null
                    for (const scope of [state === 'payee' ? 'waiting' : 'ongoing', 'all']) {
                        let page = 1, result
                        do {
                            result = await consultations.getTableauMedecin({ scope, search_item: row.patient.code || row.patient.nom,
                                page, access_token: access.data.access_token })
                            if (!current()) return
                            found = (result.data || []).find(item => Number(item.consultation_id) === Number(row.prochaine.consultation_id))
                            page++
                        } while (!found && page <= Number(result.last_page || 1))
                        if (found) break
                    }
                    if (!found) throw Error('Cette consultation n’est plus disponible dans le tableau. Actualisez les rééducations.')
                    if (state === 'payee') return this.$refs.demarrage.ouvrir_demarrage_tableau(found)
                    return this.navigate(found)
                }
                if (actionFor(row) === 'Ouvrir le dossier') return this.$router.push('/patients/' + row.patient.id + '/historique')
                const token = await clinicalToken(current)
                const detail = await api.getReeducation(row.id, token)
                if (!current()) return
                const data = detail.data
                clinicNow(new Date(), data.fuseau)
                this.reprogram = ['planifiee_attente_caisse', 'heure_a_fixer', 'planifiee_payee_avance'].includes(state)
                this.rdvTarget = { patient: row.patient, consultation: { id: data.bilan.consultation_id }, rendez_vous_id: row.prochaine.rendez_vous_id }
                await this.$nextTick()
                if (current()) this.$refs.rendezVous.ouvrir({ kine: true, fuseau: data.fuseau, date: nextSessionDate(row.frequence, data.fuseau) })
            } catch (error) { if (current()) this.error = message(error) }
            finally { if (!this._isDestroyed) this.actionLoading = null }
        }
    }
}
</script>
<style scoped lang="scss">
// Mêmes styles que la carte « Consultations » du tableau médecin (importés, pas recopiés).
@import '@/views/tableau-de-bord/dashboard-medecin/consultations-medecin/consultations-medecin.scss';
.kine-dashboard progress { display: block; width: 100%; max-width: 160px; margin-top: 6px; accent-color: #2453a6; }
</style>
